import { Link } from "@tvg/fuse/link";
import { StandardLinkProps } from "@tvg/fuse/link/standard-link";
import React, { useCallback } from "react";
import styles from "../Submenu.module.css";

// Padding to ensure focused elements aren't flush against container edges
const FOCUS_SCROLL_PADDING = 8;

type SectionGroupLinkProps = StandardLinkProps & {
  style?: React.CSSProperties;
  color?: string;
  onFocus?: React.FocusEventHandler<HTMLElement>;
};

const SectionGroupLink = (props: SectionGroupLinkProps) => {
  // CSSProperties + Record<string, string> allows custom CSS variables
  const mergedStyle: React.CSSProperties & Record<string, string> = {};

  // Only override color if explicitly provided
  if (props.color) {
    mergedStyle["--link-color-override"] = props.color;
  }

  // Merge any style passed from parent
  if (props.style) {
    Object.assign(mergedStyle, props.style);
  }

  const handleFocus = useCallback<React.FocusEventHandler<HTMLElement>>(
    (e) => {
      const target = e.currentTarget as HTMLElement;
      // Ensure focused link is visible inside the scrollable submenu container.
      // Use manual scrolling so the element isn't hidden behind the sticky
      // "See all ..." footer (submenu__allDepartmentsLinkWrapper).
      const scrollParent = target.closest(`.${styles.submenu__nav}`);
      if (scrollParent && scrollParent instanceof HTMLElement) {
        const parentRect = scrollParent.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        // Sticky footer that can overlap content near the bottom
        const stickyFooter = scrollParent.querySelector(
          `.${styles.submenu__allDepartmentsLinkWrapper}`,
        ) as HTMLElement | null;

        // Compute the bottom limit available for content visibility.
        // If sticky footer exists, its top edge becomes the effective bottom limit.
        let visibleBottom = parentRect.bottom;
        if (stickyFooter) {
          const stickyRect = stickyFooter.getBoundingClientRect();
          visibleBottom = Math.min(visibleBottom, stickyRect.top);
        }

        // Scroll down if the element is obscured by the sticky footer or below view
        if (targetRect.bottom > visibleBottom - FOCUS_SCROLL_PADDING) {
          const delta =
            targetRect.bottom - (visibleBottom - FOCUS_SCROLL_PADDING);
          scrollParent.scrollTop += Math.ceil(delta);
        }

        // Scroll up if the element is above the visible area
        if (targetRect.top < parentRect.top + FOCUS_SCROLL_PADDING) {
          const delta = parentRect.top + FOCUS_SCROLL_PADDING - targetRect.top;
          scrollParent.scrollTop -= Math.ceil(delta);
        }
      }
      props.onFocus?.(e);
    },
    [props.onFocus],
  );

  return (
    <Link
      size="medium"
      {...props}
      onFocus={handleFocus}
      style={mergedStyle}
      className={`${styles.submenu__groupLink} submenu__groupLink`}
    />
  );
};

export default SectionGroupLink;
